// core/stores/commentStore.ts — Zustand store for the flat comment list.
//
// Hydrated at module init from the typed seed (seedComments). Services mutate
// this store; hooks read it reactively. Components must use hooks.
//
// Flatten rule: parentId always points to a top-level comment id. The store
// enforces no business rules — that is the service's responsibility.

import { create } from 'zustand';
import type { Comment } from '@/core/models/comment';
import { seedComments } from '@/core/data/comments';
import { bootSeed } from './bootSeed';

interface CommentState {
  comments: Comment[];

  // Replace the entire comment list (used by commentsService._resetForTest).
  setComments: (comments: Comment[]) => void;

  // Prepend a new comment to the list (mirrors legacy unshift behaviour).
  addComment: (comment: Comment) => void;

  // Apply a patch to a comment by id; no-op if not found.
  patchComment: (id: string, patch: Partial<Comment>) => void;
}

export const useCommentStore = create<CommentState>()((set) => ({
  // Deep-clone the seed so test resets don't mutate the imported const.
  // Empty in the running app (see bootSeed.ts); the seed only exists under the test runner.
  comments: bootSeed(seedComments).map((c) => ({ ...c, editHistory: [...c.editHistory] })),

  setComments: (comments) => set({ comments }),

  addComment: (comment) =>
    set((s) => ({ comments: [comment, ...s.comments] })),

  patchComment: (id, patch) =>
    set((s) => ({
      comments: s.comments.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    })),
}));
